import React, { useState } from "react";
import { api } from "../lib/api";
import { Check } from "lucide-react";

export const OWNER_PRESETS = ["Drew", "Pete", "A'Lecia", "Alexis", "Heather"];

/**
 * Owner picker — preset dropdown + inline input when "Other" is selected.
 * Saves immediately to the backend via PATCH /admin/submissions/{id}/meta.
 *
 * Props:
 *  - submissionId
 *  - value           current owner string (preset name, custom name, or null)
 *  - onSaved(next)   called with the saved owner string
 *  - compact         small variant for tight table cells
 */
export default function OwnerPicker({ submissionId, value, onSaved, compact = false }) {
    const isCustom = !!value && !OWNER_PRESETS.includes(value);
    const [mode, setMode] = useState(isCustom ? "Other" : value || "");
    const [custom, setCustom] = useState(isCustom ? value : "");
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    const persist = async (owner) => {
        setSaving(true);
        try {
            await api.patch(`/admin/submissions/${submissionId}/meta`, { owner });
            setSaved(true);
            setTimeout(() => setSaved(false), 1200);
            onSaved && onSaved(owner);
        } catch (e) {
            // surface but don't blow up the row
            console.error("owner save failed", e);
        } finally {
            setSaving(false);
        }
    };

    const onSelect = (e) => {
        const v = e.target.value;
        setMode(v);
        if (v === "") {
            persist("");
        } else if (v !== "Other") {
            setCustom("");
            persist(v);
        }
        // "Other" waits for blur on custom input
    };

    const onCustomBlur = () => {
        const v = custom.trim();
        if (v) persist(v);
    };

    const selectClass = `field-input ${compact ? "py-1 text-sm" : ""}`;
    const inputClass = `field-input ${compact ? "py-1 text-sm" : ""}`;
    const selectStyle = compact ? { minWidth: 130 } : undefined;

    return (
        <div className="flex items-center gap-2" data-testid={`owner-picker-${submissionId}`}>
            <select
                className={selectClass}
                style={selectStyle}
                value={mode}
                onChange={onSelect}
                disabled={saving}
                data-testid={`owner-select-${submissionId}`}
            >
                <option value="">— Unassigned —</option>
                {OWNER_PRESETS.map((p) => (
                    <option key={p} value={p}>
                        {p}
                    </option>
                ))}
                <option value="Other">Other…</option>
            </select>
            {mode === "Other" && (
                <input
                    type="text"
                    placeholder="Type name…"
                    className={inputClass}
                    value={custom}
                    onChange={(e) => setCustom(e.target.value)}
                    onBlur={onCustomBlur}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") e.target.blur();
                    }}
                    disabled={saving}
                    data-testid={`owner-custom-${submissionId}`}
                    style={{ minWidth: 110 }}
                />
            )}
            {saved && <Check size={14} className="text-ash-orange" aria-label="Saved" />}
        </div>
    );
}
